import React from 'react';

import { HStack, Icon, Skeleton, Stack, Tab, TabList, Tabs, Text, Tooltip } from '@ecoinvent/ui';
import { FiClock, FiLock } from 'react-icons/fi';
import { useAuth } from 'react-oidc-context';
import { Link, useLocation } from 'react-router';

import useMenuUrlLinks from 'hooks/useMenuUrlLinks';
import { useIsShowingV4 } from 'hooks/useV4';

type Props = { isLoading?: boolean; hasAccess?: boolean };

const DatasetTabs = ({ isLoading, hasAccess }: Props) => {
  const { isAuthenticated } = useAuth();
  const isShowingV4 = useIsShowingV4();
  const { items: menuItems, v4Items: menuV4Items, handleRouting } = useMenuUrlLinks();

  const location = useLocation();

  // When on supply-chain, highlight whichever tab the user came from (or Flows as fallback).
  const pathnameForIndex = location.pathname.endsWith('/supply-chain')
    ? ((location.state as { from?: string } | null)?.from ?? location.pathname.replace('/supply-chain', '/flows'))
    : location.pathname;

  const disabledTabs = ['History', 'Export', 'LCI results'];
  const guestAccessibleTabs = isAuthenticated ? ['Documentation', 'Impact assessment'] : ['Documentation'];

  const displayItems = !hasAccess
    ? [
        ...menuItems.filter((item) => guestAccessibleTabs.includes(item.text)),
        ...menuItems.filter((item) => !guestAccessibleTabs.includes(item.text)),
      ]
    : menuItems;

  return (
    <Stack w="100%" overflowX={'auto'}>
      <Skeleton isLoaded={!isLoading} alignSelf={'flex-start'} overflow={'auto'}>
        <Tabs
          overflowY="hidden"
          alignSelf={'flex-start'}
          size="sm"
          index={
            !isShowingV4
              ? displayItems.map((item) => item.path).indexOf(pathnameForIndex)
              : menuV4Items.map((item) => item.path).indexOf(pathnameForIndex)
          }
        >
          <TabList maxW="100%" w="max-content" data-tour-target="dataset-tabs">
            {!isShowingV4
              ? displayItems.map((item) => {
                  const shouldBeDisabled = !guestAccessibleTabs.includes(item.text) && !hasAccess;
                  const isSelected = location.pathname === item.path;
                  return (
                    <Tab
                      key={item.text}
                      as={Link}
                      to={item.path}
                      onClick={() => handleRouting(item)}
                      fontWeight={isSelected ? 'semibold' : 'normal'}
                      isDisabled={shouldBeDisabled}
                      {...(item.text === 'Exchanges' && { 'data-tour-target': 'exchanges-tab' })}
                    >
                      <HStack>
                        {shouldBeDisabled && <Icon as={FiLock} />}
                        <Text>{item.text}</Text>
                      </HStack>
                    </Tab>
                  );
                })
              : menuV4Items.map((item) => {
                  const shouldBeDisabled = (item.text !== 'Documentation' && !hasAccess) || disabledTabs.includes(item.text);
                  const isSelected = location.pathname === item.path;
                  return (
                    <Tab
                      key={item.text}
                      as={Link}
                      to={item.path}
                      onClick={() => handleRouting(item)}
                      fontWeight={isSelected ? 'semibold' : 'normal'}
                      isDisabled={shouldBeDisabled}
                    >
                      {shouldBeDisabled ? (
                        <Tooltip hasArrow label="Coming soon" placement="top">
                          <HStack>
                            <Icon as={FiClock} />
                            <Text>{item.text}</Text>
                          </HStack>
                        </Tooltip>
                      ) : (
                        <HStack>
                          <Text>{item.text}</Text>
                        </HStack>
                      )}
                    </Tab>
                  );
                })}
          </TabList>
        </Tabs>
      </Skeleton>
    </Stack>
  );
};

export default DatasetTabs;
